import React, { useEffect, useState } from "react";
import { getAllTimetableDays, deleteTimetableDay } from "../services/adminTimeTableServices";
import AdminPageLayout from "../components/AdminPageLayout";
import TimetableDayAccordion from "../components/time_table/TimetableDayAccordion";
import ScheduleDayDrawer from "../components/time_table/ScheduleDayDrawer";
import TemplateManagerModal from "../components/time_table/TemplateManagerModal";
import { utcToLocalDate } from "../../utils/dateTimeUtils";
import "../components/time_table/timetable.css";

const adminTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const AdminTimetablePage = () => {
  const [days, setDays]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [selected, setSelected]         = useState(new Set());
  const [deleting, setDeleting]         = useState(false);

  useEffect(() => { loadTimetable(); }, []);

  const updateSessionInPlace = (dayId, updatedSession) => {
    setDays((prev) => prev.map((day) =>
      day.id !== dayId ? day : {
        ...day,
        sessions: day.sessions
          .map((s) => s.id === updatedSession.id ? { ...s, ...updatedSession } : s)
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime)),
      }
    ));
  };

  const loadTimetable = async () => {
    setLoading(true);
    try {
      const res = await getAllTimetableDays();
      const converted = res.data.map((day) => ({
        ...day,
        localDate: utcToLocalDate(day.date, adminTimeZone),
        utcDate:   day.date,
        sessions:  [...(day.sessions || [])].sort((a, b) => new Date(a.startTime) - new Date(b.startTime)),
      }));
      converted.sort((a, b) => new Date(a.utcDate) - new Date(b.utcDate));
      setDays(converted);
      setSelected(new Set());
    } catch {}
    finally { setLoading(false); }
  };

  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected(selected.size === days.length ? new Set() : new Set(days.map((d) => d.id)));

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const count = selected.size;
    if (!window.confirm(`Delete ${count} day${count > 1 ? "s" : ""} and all their sessions? This cannot be undone.`)) return;
    setDeleting(true);
    await Promise.all([...selected].map((id) => deleteTimetableDay(id).catch(() => {})));
    setDeleting(false);
    loadTimetable();
  };

  const allSelected = days.length > 0 && selected.size === days.length;
  const someSelected = selected.size > 0;

  const actions = (
    <>
      <button className="adm-btn adm-btn-ghost" onClick={() => setTemplateOpen(true)}>
        ⊞ Manage Templates
      </button>
      <button className="adm-btn adm-btn-primary" onClick={() => setDrawerOpen(true)}>
        + Schedule New Day
      </button>
    </>
  );

  return (
    <AdminPageLayout
      title="Timetable Management"
      subtitle="Schedule sessions, manage templates, and control visibility."
      actions={actions}
    >
      {loading ? (
        <div className="adm-loading">Loading timetable…</div>
      ) : days.length === 0 ? (
        <div className="adm-empty">
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <p style={{ margin: 0, fontWeight: 600, color: "#475569" }}>No days scheduled yet</p>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "#94a3b8" }}>Click "Schedule New Day" to get started.</p>
        </div>
      ) : (
        <div>
          {/* Select-all row */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, padding: "0 4px" }}>
            <input
              type="checkbox"
              className="tt-day-checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              title={allSelected ? "Deselect all" : "Select all"}
            />
            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
              {someSelected ? `${selected.size} of ${days.length} selected` : "Select all"}
            </span>
          </div>

          {/* Bulk action toolbar */}
          {someSelected && (
            <div className="tt-bulk-bar">
              <span className="tt-bulk-bar-text">
                {selected.size} day{selected.size > 1 ? "s" : ""} selected
              </span>
              <button
                className="adm-btn adm-btn-ghost adm-btn-sm"
                onClick={() => setSelected(new Set())}
              >
                Clear
              </button>
              <button
                className="adm-btn adm-btn-danger adm-btn-sm"
                onClick={handleBulkDelete}
                disabled={deleting}
              >
                {deleting ? "Deleting…" : `🗑 Delete ${selected.size} Day${selected.size > 1 ? "s" : ""}`}
              </button>
            </div>
          )}

          {/* Day list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {days.map((day) => (
              <TimetableDayAccordion
                key={day.id}
                day={day}
                reload={loadTimetable}
                onSessionUpdated={(s) => updateSessionInPlace(day.id, s)}
                selected={selected.has(day.id)}
                onToggleSelect={() => toggleSelect(day.id)}
              />
            ))}
          </div>
        </div>
      )}

      {drawerOpen   && <ScheduleDayDrawer onClose={() => setDrawerOpen(false)} reload={loadTimetable} />}
      {templateOpen && <TemplateManagerModal onClose={() => setTemplateOpen(false)} days={days} />}
    </AdminPageLayout>
  );
};

export default AdminTimetablePage;
